import { CenteredPage, urlUtil } from '@cmg/common';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import externalRouteFactory from '../../../common/util/externalRouteFactory';
import type { PasswordReset } from '../../../types/domain/self/PasswordReset';
import ResetPasswordForm from './components/ResetPasswordForm';
import { resetPassword, resetState, selectError, selectSubmitting, selectSuccess } from './ducks';

// eslint-disable-next-line
// TODO: create task for "uplifting" ResetPassword, or erase this route, if forgot-password is enough

/**
 * Displays a centered reset password form. You can get to this route from a reset password
 * email.
 * If you come here without having the necessary info to perform a reset, then it
 * redirects to login.
 */
export const ResetPasswordRouteComponent: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const error = useSelector(selectError);
  const success = useSelector(selectSuccess);
  const submitting = useSelector(selectSubmitting);
  const { code: token, userId } = urlUtil.queryParse<{ code?: string; userId?: string }>(
    location.search
  );

  useEffect(() => {
    if (!userId || !token) {
      window.location.replace(externalRouteFactory.ecmSpa.getUrl());
    }

    return () => {
      dispatch(resetState());
    };
  }, [dispatch, token, userId]);

  const handleSubmit = useCallback(
    (values: PasswordReset) => {
      dispatch(resetPassword(values));
    },
    [dispatch]
  );

  // Didn't come here directly from email, so kick em out
  if (!userId || !token) {
    return null;
  }

  return (
    <CenteredPage title="Password reset">
      <ResetPasswordForm
        onSubmit={handleSubmit}
        error={error}
        success={success}
        submitting={submitting}
        token={token}
        userId={userId}
      />
    </CenteredPage>
  );
};

export default ResetPasswordRouteComponent;
